/**
 * Apollo Link: AST-rewrites every top-level Query field selection that accepts
 * `status: PublicationStatus` to add `status: DRAFT`. Strapi v5 returns the
 * draft revision when `status: DRAFT` is supplied; otherwise the published one.
 *
 * Active only when the per-request "preview" flag is true. The flag is read
 * from Apollo Link operation context (set by setContext in the client config),
 * never from module-scope state, so this link is safe to share across SSR
 * requests when @nuxtjs/apollo creates one ApolloClient per request.
 *
 * The set of safe-to-rewrite field names is read from the build-time-
 * generated snapshot at motec/apollo/draft-fields.generated.json (produced
 * by motec/scripts/generate-draft-fields.js). This is the long-term-stable
 * "Option C" design: zero runtime introspection, build-pod is the only thing
 * that talks to Strapi for this concern, drift is visible in the diff of
 * the committed JSON.
 *
 * Failure-mode summary:
 *   - Unknown field in the query (custom plugin not in snapshot): no inject,
 *     query runs against published. Editor sees published content for that
 *     specific data; explicit rebuild + commit of generated snapshot fixes it.
 *   - Field has explicit `status:` arg already: caller's choice respected.
 *   - Empty snapshot (fallback mode): no inject anywhere. Editor sees a
 *     working public site rather than a broken preview. Safe default.
 */

import { ApolloLink } from 'apollo-link'
import { visit } from 'graphql/language/visitor'
import generatedDraftFields from '../draft-fields.generated.json'

/**
 * Build the AST node for `status: DRAFT`.
 * Fresh node per call so we never share node identity across visited trees.
 */
function makeStatusDraftArg () {
  return {
    kind: 'Argument',
    name: { kind: 'Name', value: 'status' },
    value: { kind: 'EnumValue', value: 'DRAFT' }
  }
}

/**
 * Walk a GraphQL Document AST and inject `status: DRAFT` on every top-level
 * Field of every `query` operation, where the field name appears in the
 * supplied allowlist and the call doesn't already specify `status`.
 *
 * `allowedFields` is `{ fieldName: true }` for O(1) presence check.
 *
 * Nested fields are not rewritten — Strapi v5 propagates the parent's
 * status to relations and components within the same document.
 *
 * Returns a new document (never mutates the input).
 */
export function injectDraftStatus (document, allowedFields) {
  const allow = allowedFields || {}
  return visit(document, {
    OperationDefinition: {
      enter (op) {
        if (op.operation !== 'query') {
          return false // skip mutations & subscriptions entirely
        }
        const newSelections = op.selectionSet.selections.map((sel) => {
          if (sel.kind !== 'Field') return sel
          const fieldName = sel.name.value
          if (fieldName.startsWith('__')) return sel
          if (!allow[fieldName]) return sel
          const existingArgs = sel.arguments || []
          if (existingArgs.some(a => a.name && a.name.value === 'status')) {
            return sel
          }
          return {
            ...sel,
            arguments: [...existingArgs, makeStatusDraftArg()]
          }
        })
        return {
          ...op,
          selectionSet: { ...op.selectionSet, selections: newSelections }
        }
      }
    }
  })
}

/**
 * Construct the Apollo Link.
 *
 * Options:
 *   - isPreviewRequest(operation) => boolean (required). The Apollo Link
 *     context for each operation tells us whether the calling SSR request
 *     is in preview mode.
 *   - allowedFields: { fieldName: true } map. Defaults to the build-time
 *     generated snapshot; override is useful only for tests.
 */
export function createDraftInjectorLink ({ isPreviewRequest, allowedFields } = {}) {
  const allow = allowedFields || (generatedDraftFields && generatedDraftFields.fields) || {}
  if (typeof isPreviewRequest !== 'function') {
    // No-op link — keeps callers safe to install unconditionally.
    return new ApolloLink((operation, forward) => forward(operation))
  }
  return new ApolloLink((operation, forward) => {
    let active = false
    try {
      active = !!isPreviewRequest(operation)
    } catch (e) {
      active = false
    }
    if (active) {
      operation.query = injectDraftStatus(operation.query, allow)
    }
    return forward(operation)
  })
}

// Exposed for unit testing
export const __test__ = { injectDraftStatus, makeStatusDraftArg, generatedDraftFields }
